import { Mesh, MathUtils } from 'three';
export type IdleExpression = 'natural' | 'right-wink-smile';

// Close, briefly hold, then open the right eye. Only after it opens,
// ease into a smile and release it completely before the next gesture.
function winkSmile(time: number) {
    const phase = ((time % 10.8) + 10.8) % 10.8 - 3.2;
    const ease = (start: number, end: number) => MathUtils.smoothstep(phase, start, end);
    return {
        wink: ease(0, .16) * (1 - ease(.26, .48)),
        smile: ease(.62, 1.17) * (1 - ease(1.77, 2.62))
    };
}

export class FacialRig {
    readonly weights: Float32Array;
    readonly names: string[];
    manual: Record<string, number> = {};
    constructor(public mesh: Mesh, public idleExpression: IdleExpression = 'natural') { this.names = Object.keys(mesh.morphTargetDictionary ?? {}); this.weights = new Float32Array(this.names.length); }
    set(name: string, value: number) { const i = this.mesh.morphTargetDictionary?.[name]; if (i !== undefined) {
        this.weights[i] = MathUtils.clamp(value, 0, 1);
        this.mesh.morphTargetInfluences![i] = this.weights[i];
    } }
    update(time: number, speaking: number, gazeX: number, gazeY: number, reduced: boolean, expressionTime = time, allowIdleExpression = true) {
        this.weights.fill(0);
        this.mesh.morphTargetInfluences?.fill(0);
        const interval = 8.37, cycle = time % interval, sequence = Math.floor(time / interval);
        const fraction = (Math.sin(sequence * 127.1) * 43758.5453) % 1;
        const onset = 4.6 + Math.abs(fraction) * 2.2;
        const blink = !reduced && cycle > onset && cycle < onset + .26 ? Math.sin((cycle - onset) / .26 * Math.PI) : 0;
        const gesture = !reduced && allowIdleExpression && speaking === 0 ? winkSmile(expressionTime) : { wink: 0, smile: 0 };
        const expressive = this.idleExpression === 'right-wink-smile';
        this.set('EyeBlinkLeft', expressive ? 0 : blink * .94);
        this.set('EyeBlinkRight', expressive ? gesture.wink : blink * .94);
        this.set('BrowRaise', speaking * .07 + (!reduced ? (Math.sin(time * .28) + 1) * .022 : 0));
        this.set('MouthSmile', .045 + speaking * .06 + (expressive ? gesture.smile * .55 : 0));
        this.set('JawOpen', speaking * .5);
        this.set('MouthWide', speaking * (Math.sin(time * 8) * .5 + .5) * .18);
        this.set('MouthFunnel', speaking * (Math.cos(time * 6) * .5 + .5) * .12);
        const x = gazeX + (reduced ? 0 : Math.sin(time * .37) * .065), y = gazeY + (reduced ? 0 : Math.sin(time * .23 + 1) * .035);
        this.set(x < 0 ? 'EyeLookLeft' : 'EyeLookRight', Math.abs(x) * .5);
        this.set(y < 0 ? 'EyeLookDown' : 'EyeLookUp', Math.abs(y) * .5);
        for (const [n, v] of Object.entries(this.manual))
            this.set(n, v);
    }
}
